/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  Brightness,
  Button,
  CardImage,
  CardText,
  CardTitle,
  CardTitleContainer,
  Content,
  Gap,
  HeaderWrapper,
  HeroSection,
  Image,
  LowBrightness,
  MainContainer,
  SectionCrafts,
  SectionCraftsCards,
  SectionCraftsRow,
  SectionPopular,
  SectionPopularCard,
  SectionPopularRow,
  StyledButton,
  StyledCardImage,
  StyledContent,
  StyledImage,
  StyledLowBrightness,
  StyledSectionCraftsCards,
  StyledSubtitle,
  StyledTitle,
  Subtitle,
  Title,
  TitleContainer,
} from "./LandingPage.styles.js";

import Header from "../../components/NewHeader/NewHeader.js";
import Footer from "../../components/Footer.js";
import SearchComponent from "./SearchComponent.js";

import images from "../../themes/images.js";

import { selectBusinessUserInfo } from "../../store/slices/businessUser/slice.js";
import { countClickAction } from "../../store/slices/common/asyncThunks.js";
import { categoriesData } from "../../store/slices/marketplace/marketplaceSlice.js";

const popular = [
  {
    id: 1,
    title: "true-blue",
    subtitle: "hydration-to-go",
    categoryLabel: "drinkware",
    category: "Drinkware",
    cardSize: "small",
    image: images.Landing1,
  },
  {
    id: 2,
    title: "piece-by-piece",
    subtitle: "show-your-brand",
    categoryLabel: "office-items",
    category: "Office Items",
    cardSize: "small",
    image: images.Landing2,
  },
  {
    id: 3,
    title: "bold-ideas",
    subtitle: "note-that-inspire",
    categoryLabel: "stationery",
    category: "Stationery",
    cardSize: "small",
    image: images.Landing3,
  },
  {
    id: 4,
    title: "tote-bags-and-backpacks",
    subtitle: "",
    categoryLabel: "bags",
    category: "Bags",
    cardSize: "big",
    image: images.Landing4,
  },
  {
    id: 5,
    title: "unique-brand-items",
    subtitle: "",
    categoryLabel: "talk-to-us",
    cardSize: "big",
    image: images.Landing5,
  },
];

const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedIn } = useSelector(selectBusinessUserInfo);

  const categories = useSelector(categoriesData);

  const [searchTerm, setSearchTerm] = useState("");

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && searchTerm !== "") {
      if (loggedIn) {
        navigate(`/admin-portal/search?searchTerm=${searchTerm}`);
      } else {
        navigate(`/search?searchTerm=${searchTerm}`);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keypress", handleKeyPress);

    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleKeyPress]);

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollUpY, setLastScrollUpY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(null);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY;
      const lastScrollTop = lastScrollTopRef.current;

      if (currentScrollTop === 0) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
        setScrollDirection(null);
        lastScrollTopRef.current = currentScrollTop;
        return;
      }

      if (currentScrollTop > lastScrollTop) {
        setScrollDirection("down");
        setLastScrollUpY(currentScrollTop);
      } else {
        setScrollDirection("up");
      }

      if (scrollDirection === "up" && currentScrollTop < lastScrollUpY - 350) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
      }

      if (
        scrollDirection === "down" &&
        currentScrollTop > lastScrollUpY + 350
      ) {
        setHeaderVisible(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    const handleScrollDebounced = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener("scroll", handleScrollDebounced);

    return () => {
      window.removeEventListener("scroll", handleScrollDebounced);
    };
  }, [scrollDirection]);

  return (
    <MainContainer $column>
      <HeaderWrapper
        style={{
          opacity: headerVisible ? 1 : 0,
          marginTop: headerVisible ? 0 : -150,
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Header />
      </HeaderWrapper>
      <StyledContent>
        <Content>
          <SearchComponent />

          <HeroSection>
            <Brightness />

            <TitleContainer>
              <StyledTitle>{t("quality-pleasures")}</StyledTitle>
              <StyledSubtitle>{t("wrap-yourself-in-happiness")}</StyledSubtitle>
              <StyledButton
                onClick={() => {
                  window.scrollTo(0, 0);
                  if (loggedIn) {
                    navigate("/admin-portal/marketplace");
                  } else {
                    navigate("/marketplace");
                  }
                }}
              >
                {t("shop-now")}
              </StyledButton>
            </TitleContainer>
            <StyledImage
              src={images.GirlWithJacket}
              alt="Girl with Jacket - Ygii Business Portal"
            />
          </HeroSection>

          <SectionPopular>
            <Title $small>{t("popular-on-ygii-marketplace")}</Title>
            <SectionPopularRow>
              {popular.map((product) => {
                const category = categories.find(
                  (cat) => cat.name === product.category
                );

                return (
                  <SectionPopularCard
                    $big={product.cardSize === "big"}
                    key={product.id}
                  >
                    <Gap>
                      <CardTitleContainer $big={product.cardSize === "big"}>
                        <CardTitle>{t(product.title)}</CardTitle>
                        <Subtitle $small>{t(product.subtitle)}</Subtitle>
                      </CardTitleContainer>
                      {product.categoryLabel === "talk-to-us" ? (
                        <Button
                          onClick={() => {
                            dispatch(countClickAction("demoRequest"));
                            window.open(
                              "https://calendly.com/abdulkareem-2/demo",
                              "_blank"
                            );
                          }}
                        >
                          {t(product.categoryLabel)}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            window.scrollTo(0, 0);
                            if (loggedIn) {
                              if (category?.id) {
                                navigate(
                                  `/admin-portal/marketplace?category=${category?.id}`
                                );
                              } else {
                                navigate(`/admin-portal/marketplace`);
                              }
                            } else {
                              if (category?.id) {
                                navigate(
                                  `/marketplace?category=${category?.id}`
                                );
                              } else {
                                navigate(`/marketplace`);
                              }
                            }
                          }}
                        >
                          {t(`shop-${product.categoryLabel}`)}
                        </Button>
                      )}
                    </Gap>

                    <CardImage
                      src={product.image}
                      alt={`Landing Page Image${product.id}`}
                    />
                    <LowBrightness />
                  </SectionPopularCard>
                );
              })}
            </SectionPopularRow>
          </SectionPopular>

          <SectionCrafts>
            <Title $small style={{ marginTop: -20, marginBottom: 20 }}>
              {t("year-of-crafts")}
            </Title>
            <SectionCraftsRow>
              <SectionCraftsCards $big $none>
                <Image src={images.Landing6} alt="Landing Page Crafts Image" />
              </SectionCraftsCards>
              <StyledSectionCraftsCards>
                <StyledLowBrightness />
                <StyledCardImage
                  src={images.Landing6}
                  alt="Landing Page Crafts Image"
                />

                <Gap>
                  <CardTitle>{t("crafts-spotlight")}</CardTitle>
                  <CardText>{t("crafts-text")}</CardText>
                  <Button
                    onClick={() => {
                      window.scrollTo(0, 0);
                      if (loggedIn) {
                        navigate("/admin-portal/marketplace");
                      } else {
                        navigate("/marketplace");
                      }
                    }}
                  >
                    {t("shop-now")}
                  </Button>
                </Gap>
              </StyledSectionCraftsCards>
            </SectionCraftsRow>
          </SectionCrafts>
        </Content>
      </StyledContent>
      <Footer />
    </MainContainer>
  );
};

export default LandingPage;
